import { useState } from 'react';
import { CheckOutlined, StopOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router';
import { useEstadoDeLista } from '@titularis/ui';
import type { Report, ReportKind, ReportStatus } from '@titularis/api-client';
import { api } from '../api';
import { formatarData } from '../dominio';

/**
 * Fila de erros reportados.
 *
 * O reporte aponta para a **versão** que o aluno viu, não para a questão de
 * hoje: se o texto já foi corrigido, o administrador precisa saber o que estava
 * escrito quando o erro foi apontado. Por isso a linha mostra o Código de
 * Versionamento junto do enunciado.
 */
const TIPO_LABEL: Record<ReportKind, string> = {
  statement: 'Enunciado ou itens',
  answer_key: 'Gabarito errado',
  classification: 'Classificação errada',
  annulled: 'Questão anulada',
  outdated: 'Desatualizada',
  duplicated: 'Duplicada',
  other: 'Outro',
};

const TIPO_COR: Record<ReportKind, string> = {
  statement: 'orange',
  answer_key: 'red',
  classification: 'blue',
  annulled: 'purple',
  outdated: 'gold',
  duplicated: 'cyan',
  other: 'default',
};

const SITUACOES: { value: ReportStatus | 'todos'; label: string }[] = [
  { value: 'open', label: 'Abertos' },
  { value: 'resolved', label: 'Resolvidos' },
  { value: 'dismissed', label: 'Descartados' },
  { value: 'todos', label: 'Todos' },
];

/** Prévia em texto: a fila mostra o começo do enunciado, não a formatação. */
const emTexto = (html: string | null): string => {
  if (!html) return '';
  const texto = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return texto.length > 120 ? `${texto.slice(0, 120)}...` : texto;
};

export function ReportesPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const { ler, filtrar, irParaPagina } = useEstadoDeLista();

  /*
   * Situação e página na URL. Aqui isso vale dobrado: a fila é trabalho em
   * turno, e "abertos, página 3" é onde a pessoa parou.
   *
   * Sem valor na URL a fila abre nos abertos, que é o padrão de quem vem
   * trabalhar - e por isso `open` não é escrito na barra de endereço.
   */
  const situacao = (SITUACOES.map((item) => item.value).find(
    (valor) => valor === ler.texto('situacao'),
  ) ?? 'open') as ReportStatus | 'todos';
  const page = ler.pagina();
  const [tratando, setTratando] = useState<{ reporte: Report; status: 'resolved' | 'dismissed' }>();
  const [nota, setNota] = useState('');

  const reportes = useQuery({
    queryKey: ['reports', { situacao, page }],
    queryFn: () =>
      api.listReports({
        status: situacao === 'todos' ? undefined : situacao,
        page,
        pageSize: 20,
      }),
  });

  const tratar = useMutation({
    mutationFn: () => api.resolveReport(tratando!.reporte.id, tratando!.status, nota || null),
    onSuccess: async () => {
      message.success(tratando!.status === 'resolved' ? 'Reporte resolvido.' : 'Reporte descartado.');
      setTratando(undefined);
      setNota('');
      await queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Erros reportados
        </Typography.Title>
        <Typography.Text type="secondary">
          O que os alunos apontaram, sempre preso à versão da questão que eles viram.
        </Typography.Text>
      </div>

      {reportes.error ? (
        <Alert type="error" showIcon title={(reportes.error as Error).message} />
      ) : null}

      <Card size="small">
        <Flex justify="space-between" align="center" gap={12} wrap style={{ marginBottom: 16 }}>
          <Segmented
            value={situacao}
            onChange={(valor) => filtrar({ situacao: valor === 'open' ? undefined : valor })}
            options={SITUACOES}
          />
          {reportes.data ? (
            <Typography.Text type="secondary">{reportes.data.total} reporte(s)</Typography.Text>
          ) : null}
        </Flex>

        <Table<Report>
          rowKey="id"
          loading={reportes.isPending}
          dataSource={reportes.data?.items ?? []}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  situacao === 'open' ? 'Nenhum reporte em aberto.' : 'Nada com esse filtro.'
                }
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize: reportes.data?.pageSize ?? 20,
            total: reportes.data?.total ?? 0,
            onChange: (pagina: number) => irParaPagina(pagina),
            hideOnSinglePage: true,
            showSizeChanger: false,
          }}
          columns={[
            {
              title: 'Tipo',
              dataIndex: 'kind',
              width: 190,
              render: (kind: ReportKind) => <Tag color={TIPO_COR[kind]}>{TIPO_LABEL[kind]}</Tag>,
            },
            {
              title: 'Questão',
              render: (_, reporte) => (
                <Space orientation="vertical" size={2}>
                  <Typography.Text>{emTexto(reporte.statementHtml) || 'Sem enunciado'}</Typography.Text>
                  <Space size={6} wrap>
                    <Typography.Text type="secondary">{reporte.examName}</Typography.Text>
                    <Typography.Text code style={{ whiteSpace: 'nowrap' }}>
                      {reporte.versionCode}
                    </Typography.Text>
                  </Space>
                </Space>
              ),
            },
            {
              title: 'O que o aluno disse',
              dataIndex: 'message',
              width: 260,
              render: (texto: string | null) =>
                texto ?? <Typography.Text type="secondary">Sem descrição</Typography.Text>,
            },
            {
              title: 'Quem reportou',
              width: 200,
              render: (_, reporte) => (
                <Space orientation="vertical" size={0}>
                  <Typography.Text>{reporte.userName ?? 'Conta excluída'}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {reporte.userEmail}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {formatarData(reporte.createdAt)}
                  </Typography.Text>
                </Space>
              ),
            },
            {
              title: '',
              width: 230,
              render: (_, reporte) => (
                <Space size={4} wrap>
                  <Button size="small" onClick={() => void navigate(`/questoes/${reporte.questionId}`)}>
                    Abrir questão
                  </Button>
                  {reporte.status === 'open' ? (
                    <>
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        aria-label="Marcar como resolvido"
                        onClick={() => setTratando({ reporte, status: 'resolved' })}
                      />
                      <Button
                        size="small"
                        icon={<StopOutlined />}
                        aria-label="Descartar reporte"
                        onClick={() => setTratando({ reporte, status: 'dismissed' })}
                      />
                    </>
                  ) : (
                    <Tag color={reporte.status === 'resolved' ? 'green' : 'default'}>
                      {reporte.status === 'resolved' ? 'Resolvido' : 'Descartado'}
                    </Tag>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={Boolean(tratando)}
        title={tratando?.status === 'resolved' ? 'Resolver reporte' : 'Descartar reporte'}
        okText={tratando?.status === 'resolved' ? 'Resolver' : 'Descartar'}
        cancelText="Cancelar"
        confirmLoading={tratar.isPending}
        onCancel={() => {
          setTratando(undefined);
          setNota('');
        }}
        onOk={() => tratar.mutate()}
      >
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            {tratando?.status === 'resolved'
              ? 'Registre o que foi corrigido. A nota fica no histórico do reporte.'
              : 'Registre por que o reporte não procede.'}
          </Typography.Text>
          <Input.TextArea
            rows={3}
            maxLength={2000}
            value={nota}
            onChange={(evento) => setNota(evento.target.value)}
            placeholder="Opcional"
          />
        </Space>
      </Modal>
    </Space>
  );
}
